import React, { useState, memo, useCallback } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import NotificationBell from './NotificationBell';
import NotificationDetailModal from './NotificationDetailModal';
import LanguageSelector from './LanguageSelector';
import type { NotificationData } from '../types/notification';
import { notificationAPI } from '../utils/api';

const drawerWidth = 272;

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
}

const Layout: React.FC<LayoutProps> = memo(({ children, title }) => {
  const { t } = useTranslation('pages');
  const displayTitle = title || t('dashboard.title');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationData | null>(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [resolvingNotification, setResolvingNotification] = useState<NotificationData | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const handleDrawerClose = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const handleNotificationClick = useCallback((notification: NotificationData) => {
    setSelectedNotification(notification);
    setNotificationModalOpen(true);
  }, []);

  const handleNotificationModalClose = useCallback(() => {
    setNotificationModalOpen(false);
  }, []);

  const updateSelectedNotification = useCallback((id: number, updates: Partial<NotificationData>) => {
    setSelectedNotification(prev => prev?.id === id ? { ...prev, ...updates } : prev);
  }, []);

  const handleAcknowledge = useCallback(async (notification: NotificationData, event?: React.MouseEvent) => {
    event?.stopPropagation();
    try {
      const response = await notificationAPI.acknowledgeNotification(notification.id);
      updateSelectedNotification(notification.id, {
        status: response.data?.status || 'acknowledged',
        isRead: true,
        acknowledgedAt: response.data?.acknowledged_at ?? new Date().toISOString(),
        acknowledgedBy: response.data?.acknowledged_by ?? notification.acknowledgedBy,
      });
    } catch (error) {
      console.error('Failed to acknowledge notification:', error);
    }
  }, [updateSelectedNotification]);

  const handleResolveClick = useCallback((notification: NotificationData, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setResolvingNotification(notification);
    setResolutionNote(notification.resolutionNote || '');
    setResolveDialogOpen(true);
  }, []);

  const handleResolveSubmit = useCallback(async () => {
    if (!resolvingNotification) return;

    try {
      const response = await notificationAPI.resolveNotification(resolvingNotification.id, resolutionNote || undefined);
      updateSelectedNotification(resolvingNotification.id, {
        status: response.data?.status || 'resolved',
        isRead: true,
        resolvedAt: response.data?.resolved_at ?? new Date().toISOString(),
        resolvedBy: response.data?.resolved_by ?? resolvingNotification.resolvedBy,
        resolutionNote: response.data?.resolution_note ?? (resolutionNote || null),
      });
      setResolveDialogOpen(false);
      setResolvingNotification(null);
      setResolutionNote('');
    } catch (error) {
      console.error('Failed to resolve notification:', error);
    }
  }, [resolutionNote, resolvingNotification, updateSelectedNotification]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar open={mobileOpen} onClose={handleDrawerClose} />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
          maxWidth: '100%',
          overflowX: 'hidden',
          background: 'radial-gradient(circle at top right, rgb(var(--an-primary-rgb) / 0.055), var(--an-transparent) 28rem), var(--an-background)',
          minHeight: '100vh',
        }}
      >
        {/* Top AppBar for mobile */}
        {isMobile && (
          <AppBar
            position="fixed"
            sx={{
              width: '100%',
              ml: { md: `${drawerWidth}px` },
              backgroundColor: 'var(--an-surface)',
              backgroundImage: 'none',
              color: 'var(--an-text-primary)',
              borderBottom: '1px solid var(--an-border-light)',
              boxShadow: 'var(--an-shadow-sm)',
            }}
          >
            <Toolbar sx={{ minHeight: 'var(--an-topbar-height)' }}>
              <IconButton
                color="inherit"
                aria-label={t('dashboard.title')}
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, color: 'var(--an-primary)' }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                {displayTitle}
              </Typography>
              <LanguageSelector />
              <NotificationBell onNotificationClick={handleNotificationClick} />
            </Toolbar>
          </AppBar>
        )}

        {/* Top AppBar for desktop - notification only */}
        {!isMobile && (
          <AppBar
            position="fixed"
            sx={{
              width: { md: `calc(100% - ${drawerWidth}px)` },
              ml: { md: `${drawerWidth}px` },
              backgroundColor: 'var(--an-surface)',
              backgroundImage: 'none',
              color: 'var(--an-text-primary)',
              borderBottom: '1px solid var(--an-border-light)',
              boxShadow: 'var(--an-shadow-sm)',
            }}
          >
            <Toolbar sx={{ justifyContent: 'flex-end', gap: 1, minHeight: 'var(--an-topbar-height)' }}>
              <LanguageSelector />
              <NotificationBell onNotificationClick={handleNotificationClick} />
            </Toolbar>
          </AppBar>
        )}

        {/* Content */}
        <Box
          sx={{
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 2, sm: 3, lg: 4 },
            mt: 'var(--an-topbar-height)'
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Notification Detail Modal */}
      <NotificationDetailModal
        open={notificationModalOpen}
        onClose={handleNotificationModalClose}
        notification={selectedNotification}
        onAcknowledge={handleAcknowledge}
        onResolve={handleResolveClick}
      />
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('notifications.resolve')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('notifications.resolveNote')}
            placeholder={t('notifications.resolveNotePlaceholder')}
            value={resolutionNote}
            onChange={(event) => setResolutionNote(event.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>{t('notifications.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={handleResolveSubmit} variant="contained">{t('notifications.resolve')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

Layout.displayName = 'Layout';

export default Layout; 
